import React, { useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IcoChev, IcoSearch, IcoX } from '../bz/icons';
import { SAMPLE_VENDOR, searchProjects } from './predictWatchlistModel';
import './predictWatchlistFilters.css';

const plural = (n, word) => `${n} ${n === 1 ? word : `${word}s`}`;

const optionShape = PropTypes.shape({
  target: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  source: PropTypes.string
});

// "Vendor", "Vendor: Red Hat" with one selected, "Vendor: 2" with several.
const buttonText = (label, options, selected) => {
  if (!selected.length) return label;
  if (selected.length > 1) return `${label}: ${selected.length}`;
  return `${label}: ${options.find(option => option.target === selected[0])?.name || selected[0]}`;
};

/**
 * One of the watch list's filter menus: a button over a popover of checkboxes,
 * one per option, each with its count (menuOptions': the bugs the other
 * filters let through that have it; an option at 0 stays, dimmed). Several
 * selected show the bugs of any of them. `search` puts a search box on top,
 * named for what it searches ("OSS", "weakness types"), focused on open;
 * `mark` draws a mark before each option. Escape or a click outside closes it;
 * Escape gives focus back to the button.
 */
export const FilterMenu = ({ label, options, selected, onChange, search, mark }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const popId = useId();
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    if (searchRef.current) searchRef.current.focus();
    else listRef.current?.querySelector('input')?.focus();
    const onPointerDown = event => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const toggleOpen = () => {
    setQuery('');
    setOpen(value => !value);
  };
  const onKeyDown = event => {
    if (event.key !== 'Escape' || !open) return;
    event.stopPropagation();
    setOpen(false);
    buttonRef.current?.focus();
  };
  const toggle = target =>
    onChange(selected.includes(target) ? selected.filter(value => value !== target) : [...selected, target]);

  const shown = search ? searchProjects(options, query) : options;

  // Escape reaches the wrapper from the button and from inside the popover.
  return (
    <div ref={rootRef} className="pd-menu" onKeyDown={onKeyDown}>
      <button
        ref={buttonRef}
        type="button"
        className={`bz-btn bz-btn--sm pd-menu__btn${selected.length ? ' pd-menu__btn--on' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        onClick={toggleOpen}
      >
        {buttonText(label, options, selected)}
        <IcoChev size={12} aria-hidden="true" />
      </button>
      {open && (
        <div id={popId} className="pd-menu__pop" role="dialog" aria-label={`${label} filter`}>
          {search && (
            <div className="pd-menu__head">
              <label className="bz-input pl-search" style={{ margin: 0 }}>
                <IcoSearch size={14} aria-hidden="true" />
                <input
                  ref={searchRef}
                  type="search"
                  value={query}
                  placeholder={`Search ${search}`}
                  aria-label={`Search ${search}`}
                  onChange={event => setQuery(event.target.value)}
                />
                {query && (
                  <button
                    type="button"
                    className="pl-search__clear"
                    aria-label={`Clear the ${search} search`}
                    onClick={() => setQuery('')}
                  >
                    <IcoX size={12} />
                  </button>
                )}
              </label>
            </div>
          )}
          <div ref={listRef} className="pd-menu__list bz-scroll">
            {shown.map(option => {
              const on = selected.includes(option.target);
              const classes = [
                'pd-menu__opt',
                on && 'pd-menu__opt--on',
                option.count === 0 && 'pd-menu__opt--zero',
                option.target === SAMPLE_VENDOR && mark && 'pd-menu__opt--sample'
              ];
              return (
                <label key={option.target} className={classes.filter(Boolean).join(' ')}>
                  <input
                    type="checkbox"
                    checked={on}
                    aria-label={`${option.name}, ${plural(option.count, 'bug')}`}
                    onChange={() => toggle(option.target)}
                  />
                  {mark && <span aria-hidden="true">{mark(option)}</span>}
                  <span className="bz-truncate">{option.name}</span>
                  <span className="pd-menu__count" aria-hidden="true">
                    {option.count}
                  </span>
                </label>
              );
            })}
            {shown.length === 0 && <div className="pd-menu__empty">Nothing matches &ldquo;{query}&rdquo;.</div>}
          </div>
          {selected.length > 0 && (
            <div className="pd-menu__foot">
              <button type="button" className="bz-btn bz-btn--ghost bz-btn--sm" onClick={() => onChange([])}>
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
FilterMenu.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(optionShape).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  search: PropTypes.string,
  mark: PropTypes.func
};

/**
 * The slim row under the filter bar: each active filter as a chip that
 * removes it ("Vendor Red Hat ×"), "Clear all" (`onClearAll`, when any filter
 * is on), and `children` at the row's end.
 */
export const ActiveFilters = ({ chips, onClearAll, children }) => (
  <div className="pd-active">
    {chips.length > 0 && (
      <ul className="pd-active__list" aria-label="Active filters">
        {chips.map(chip => (
          <li key={chip.id} className="pd-fchip">
            <span className="pd-fchip__kind">{chip.kind}</span>
            {chip.name}
            <button type="button" aria-label={`Remove ${chip.kind} ${chip.name}`} onClick={chip.onRemove}>
              <IcoX size={10} aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
    )}
    {onClearAll && (
      <button type="button" className="bz-btn bz-btn--ghost bz-btn--sm" onClick={onClearAll}>
        Clear all
      </button>
    )}
    <div className="bz-grow" />
    {children}
  </div>
);
ActiveFilters.propTypes = {
  chips: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      kind: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      onRemove: PropTypes.func.isRequired
    })
  ).isRequired,
  onClearAll: PropTypes.func,
  children: PropTypes.node
};
